import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { isHttpClientError, resolveReadableHttpErrorMessage } from "../../../lib/http/errors";
import { fetchPublicRefundGroup, findRefundMembers, recognizeRefund, submitRefund } from "../repo/ticketRefundRepo";
import { canSubmitRefund, invalidateRecognition } from "../service/submissionState";
import type { Applicant, ImagePair, MemberOption, RecognitionResult } from "../types";

/** 公开表单隔离迟到识别结果，并在同步锁内提交。 */
export function usePublicRefund(token: string) {
  const group = useQuery({ queryKey: ["ticket-refund-public", token], queryFn: () => fetchPublicRefundGroup(token), retry: false });
  const [images, setImages] = useState<ImagePair>({ ticket_image: "", refund_image: "" });
  const [binding, setBinding] = useState({ receipt: "", memberId: "" });
  const [recognized, setRecognized] = useState<RecognitionResult>();
  const [selectedMember, setSelectedMember] = useState<MemberOption>();
  const autoSelectPending = useRef(false);
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [imageErrors, setImageErrors] = useState<Partial<Record<keyof ImagePair, string>>>({});
  const version = useRef(0);
  const reading = useRef<Record<keyof ImagePair, number>>({ ticket_image: 0, refund_image: 0 });
  const [readingCount, setReadingCount] = useState(0);
  const recognitionLock = useRef(false);
  const activeReaders = useRef(0);
  const lock = useRef(false);
  const recognition = useMutation({ mutationFn: () => recognizeRefund(token, images) });
  const submission = useMutation({ mutationFn: (body: Parameters<typeof submitRefund>[1]) => submitRefund(token, body) });
  const members = useQuery({
    queryKey: ["ticket-refund-members", token, binding.receipt, name],
    queryFn: () => findRefundMembers(token, binding.receipt, name.trim()),
    enabled: Boolean(binding.receipt && name.trim()), retry: false,
  });
  useEffect(() => {
    if (!autoSelectPending.current || !binding.receipt || members.isFetching || !members.isSuccess
      || name !== recognized?.evidence.ticket.name) return;
    autoSelectPending.current = false;
    const candidates = members.data.filter((member) => member.name === name);
    if (candidates.length === 1) {
      setSelectedMember(candidates[0]);
      setBinding((previous) => ({ ...previous, memberId: candidates[0].id }));
    }
  }, [binding.receipt, members.data, members.isFetching, members.isSuccess, name, recognized]);

  /** 图片选择立即失效旧识别；同一槽位的旧文件读取不能覆盖新图。 */
  async function selectImage(kind: keyof ImagePair, file: File) {
    if (lock.current || submission.isSuccess) return;
    const stamp = ++reading.current[kind];
    version.current += 1;
    autoSelectPending.current = false;
    setSelectedMember(undefined);
    setBinding(invalidateRecognition);
    setRecognized(undefined);
    setName("");
    setError("");
    setImageErrors({});
    setImages((previous) => ({ ...previous, [kind]: "" }));
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 5 * 1024 * 1024) {
      const message = `${kind === "ticket_image" ? "第一张本人车票" : "第二张退款凭证"}：请上传 JPG、PNG 或 WebP 图片，单张不超过 5 MB`;
      setError(message);
      setImageErrors({ [kind]: message });
      return;
    }
    activeReaders.current += 1;
    setReadingCount(activeReaders.current);
    try {
      const data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error("图片读取失败，请重新选择"));
        reader.readAsDataURL(file);
      });
      if (reading.current[kind] === stamp) setImages((previous) => ({ ...previous, [kind]: data }));
    } catch (cause) {
      if (reading.current[kind] === stamp) {
        const message = `${kind === "ticket_image" ? "第一张本人车票" : "第二张退款凭证"}：${resolveReadableHttpErrorMessage(cause, "图片读取失败")}`;
        setError(message);
        setImageErrors({ [kind]: message });
      }
    } finally { activeReaders.current -= 1; setReadingCount(activeReaders.current); }
  }

  /** 识别返回时检查图片版本，避免旧图授权新图。 */
  async function recognize() {
    if (!images.ticket_image || !images.refund_image || recognitionLock.current || lock.current || activeReaders.current) return;
    recognitionLock.current = true;
    const stamp = version.current;
    autoSelectPending.current = false;
    setSelectedMember(undefined);
    setError("");
    setImageErrors({});
    setBinding(invalidateRecognition);
    setRecognized(undefined);
    try {
      const result = await recognition.mutateAsync();
      if (stamp !== version.current) return;
      autoSelectPending.current = true;
      setRecognized(result);
      setBinding({ receipt: result.receipt, memberId: "" });
      setName(result.evidence.ticket.name);
      return result;
    } catch (cause) {
      if (stamp === version.current) {
        const message = resolveReadableHttpErrorMessage(cause, "图片识别失败，请重试");
        setError(message);
        const code = isHttpClientError(cause) ? cause.code : null;
        setImageErrors({
          ...(["TICKET_REFUND_IMAGE_TICKET", "TICKET_REFUND_IMAGE_PAIR"].includes(code ?? "") ? { ticket_image: message } : {}),
          ...(["TICKET_REFUND_IMAGE_REFUND", "TICKET_REFUND_IMAGE_PAIR"].includes(code ?? "") ? { refund_image: message } : {}),
        });
      }
    } finally { recognitionLock.current = false; }
  }

  /** 输入改变就取消旧候选选择，不能把自由文本当作名单成员。 */
  function searchName(value: string) {
    autoSelectPending.current = false;
    setSelectedMember(undefined);
    setName(value);
    setBinding((old) => ({ ...old, memberId: "" }));
  }

  /** 名单必须来自当前识别凭证返回的候选。 */
  function selectMember(id: string) {
    autoSelectPending.current = false;
    const member = members.data?.find((item) => item.id === id);
    setSelectedMember(member);
    setBinding((old) => ({ ...old, memberId: member?.id ?? "" }));
    return member;
  }

  /** 同步防重入，失败保留资料，成功后禁止重复提交。 */
  async function submit(values: Omit<Applicant, "member_id" | "name">) {
    if (!canSubmitRefund({ ...binding, busy: lock.current || submission.isSuccess || readingCount > 0 })) return;
    const member = members.data?.find((item) => item.id === binding.memberId);
    if (!member) { setError("请从本团出行名单选择匹配人员"); return; }
    lock.current = true;
    setError("");
    try {
      await submission.mutateAsync({ ...images, receipt: binding.receipt, applicant: { ...values, name: member.name, member_id: member.id } });
    } catch (cause) {
      setError(resolveReadableHttpErrorMessage(cause, "提交失败，请核对资料后重试"));
    } finally { lock.current = false; }
  }
  return { group, images, imageErrors, binding, recognized, selectedMember, name, error, members, recognition, submission,
    selectImage, recognize, searchName, selectMember, submit,
    reading: readingCount > 0,
    canSubmit: canSubmitRefund({ ...binding, busy: submission.isPending || submission.isSuccess || readingCount > 0 }),
  };
}
